/** Shared constants for SEC-AI frontend */

import { tUi } from './utils/uiText';



export const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';



export const AUTH_TOKEN_KEY = 'sec_ai_access_token';

export const AUTH_PROFILE_KEY = 'sec_ai_tenant_profile';

export const ACTIVE_SESSION_KEY = 'sec_ai_active_session';

export const ACTIVE_SESSION_KEY_PREFIX = 'sec_ai_active_session';

export const getActiveSessionStorageKey = (tenantId) => (
  tenantId ? `${ACTIVE_SESSION_KEY_PREFIX}:${tenantId}` : ACTIVE_SESSION_KEY
);

/** Last active consultation session for a tenant (survives logout). */
export const getStoredActiveSession = (tenantId) => {
  if (tenantId) {
    const scoped = localStorage.getItem(getActiveSessionStorageKey(tenantId));
    if (scoped) {
      return scoped;
    }
    const legacy = localStorage.getItem(ACTIVE_SESSION_KEY);
    if (legacy) {
      localStorage.setItem(getActiveSessionStorageKey(tenantId), legacy);
      return legacy;
    }
    return null;
  }
  return localStorage.getItem(ACTIVE_SESSION_KEY);
};

export const setStoredActiveSession = (tenantId, sessionId) => {
  if (!sessionId) {
    return;
  }
  if (tenantId) {
    localStorage.setItem(getActiveSessionStorageKey(tenantId), sessionId);
  }
  localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
};

export const clearStoredActiveSessionForTenant = (tenantId) => {
  if (tenantId) {
    localStorage.removeItem(getActiveSessionStorageKey(tenantId));
  }
  localStorage.removeItem(ACTIVE_SESSION_KEY);
};

export const PRODUCT_ENTITLEMENT_VALUES = ['wraith', 'legend_parley', 'both', 'apex_elicit'];

export const normalizeProductEntitlement = (value) => {
  const cleaned = String(value || '').trim().toLowerCase();
  if (cleaned === 'apex_elicit') {
    return 'wraith';
  }
  if (PRODUCT_ENTITLEMENT_VALUES.includes(cleaned)) {
    return cleaned;
  }
  return 'wraith';
};

export const deriveAllowedModesFromEntitlement = (entitlement) => {
  const normalized = normalizeProductEntitlement(entitlement);
  if (normalized === 'both') {
    return ['nation_state', 'legend_parley'];
  }
  if (normalized === 'legend_parley') {
    return ['legend_parley'];
  }
  return ['nation_state'];
};

/** Reconcile cached auth profile fields with product_entitlement (source of truth). */
export const normalizeAuthProfile = (profile) => {
  if (!profile || typeof profile !== 'object') {
    return null;
  }
  const product_entitlement = normalizeProductEntitlement(profile.product_entitlement);
  const allowed_modes = deriveAllowedModesFromEntitlement(product_entitlement);
  const default_mode = profile.default_mode && allowed_modes.includes(profile.default_mode)
    ? profile.default_mode
    : allowed_modes[0];
  return {
    ...profile,
    product_entitlement,
    allowed_modes,
    default_mode,
  };
};

export const CONSULTANT_MODES = {
  social_engineering_consultant: {
    id: 'social_engineering_consultant',
    label: 'Apex Elicit',
    productId: 'apex_elicit',
    description: 'SE Consulting | Business Behavioral Analytics',
    supportsServices: true,
    requiresConsent: false,
  },
  nation_state: {
    id: 'nation_state',
    label: 'Legend Sovereign',
    productId: 'wraith',
    description: 'Asset Protection | Nation-State Defense',
    supportsServices: false,
    requiresConsent: true,
  },
  legend_parley: {
    id: 'legend_parley',
    label: 'Legend Parley',
    productId: 'legend_parley',
    description: 'Negotiation strategy | Hostage, crisis, and deal pathfinding',
    supportsServices: false,
    requiresConsent: false,
  },
};

export const PRODUCT_BRANDS = {
  wraith: {
    id: 'wraith',
    productName: 'Legend Sovereign',
    tagline: 'Asset Protection | Nation-State Defense',
    modeId: 'nation_state',
    logo: '/branding/legend-sovereign-wordmark-navy.png',
    accentColor: '#159A9C',
  },
  apex_elicit: {
    id: 'apex_elicit',
    productName: 'Apex Elicit',
    tagline: 'SE Consulting | Business Behavioral Analytics',
    modeId: 'social_engineering_consultant',
    logo: '/branding/apex-elicit-logo.png',
    accentColor: '#4A99B9',
  },
  legend_parley: {
    id: 'legend_parley',
    productName: 'Legend Parley',
    tagline: 'Negotiation strategy | Hostage, crisis, and deal pathfinding',
    modeId: 'legend_parley',
    logo: '/branding/legend-parley-logo.png',
    accentColor: '#159A9C',
  },
};

export const MODE_TO_PRODUCT_ID = {
  nation_state: 'wraith',
  social_engineering_consultant: 'apex_elicit',
  legend_parley: 'legend_parley',
};

export const PRODUCT_ENTITLEMENT_OPTIONS = [
  { value: 'wraith', label: 'Legend Sovereign' },
  { value: 'legend_parley', label: 'Legend Parley' },
  { value: 'both', label: 'Legend Sovereign + Parley' },
];

export const USER_PRODUCT_ENTITLEMENT_OPTIONS = [
  { value: 'wraith', label: 'Legend Sovereign' },
  { value: 'legend_parley', label: 'Legend Parley' },
  { value: 'both', label: 'Legend Sovereign + Parley' },
];

export const userProductLabel = (value) => (
  USER_PRODUCT_ENTITLEMENT_OPTIONS.find((option) => option.value === value)?.label || value || 'Legend Sovereign'
);

export const getProductBrandForMode = (modeId) => {
  const productId = MODE_TO_PRODUCT_ID[modeId] || 'apex_elicit';
  return PRODUCT_BRANDS[productId];
};

export const isParleyLicenseProfile = (authProfile) => {
  const entitlement = normalizeProductEntitlement(authProfile?.product_entitlement);
  if (entitlement === 'legend_parley') {
    return true;
  }
  const modes = deriveAllowedModesFromEntitlement(entitlement);
  return modes.length === 1 && modes[0] === 'legend_parley';
};

/** Post-login splash uses the licensed product brand (Sovereign vs Parley), not public auth branding. */
export const getSplashBrandForProfile = (authProfile) => {
  if (isParleyLicenseProfile(authProfile)) {
    return PRODUCT_BRANDS.legend_parley;
  }
  return getProductBrandForMode(getDefaultModeForProfile(authProfile));
};

export const getAllowedModes = (authProfile) => (
  deriveAllowedModesFromEntitlement(authProfile?.product_entitlement)
);

export const getDefaultModeForProfile = (authProfile) => {
  if (authProfile?.default_mode && getAllowedModes(authProfile).includes(authProfile.default_mode)) {
    return authProfile.default_mode;
  }
  return getAllowedModes(authProfile)[0] || DEFAULT_CONSULTANT_MODE;
};

export const isModeAllowedForProfile = (authProfile, modeId) => (
  getAllowedModes(authProfile).includes(modeId)
);

export const canSwitchProductMode = (authProfile) => (
  getAllowedModes(authProfile).length > 1
);

export const MODE_SPECIALTIES = {
  social_engineering_consultant: [
    {
      id: 'offense',
      label: 'Offense',
      description: 'Threat-emulation planning for authorized defensive readiness only.',
    },
    {
      id: 'defense',
      label: 'Defense',
      description: 'Defensive posture, detections, and mitigation strategy.',
    },
    {
      id: 'general_advice',
      label: 'General Advice',
      description: 'Balanced strategic and tactical consulting guidance.',
    },
  ],
  nation_state: [
    {
      id: 'tier_1_defense',
      label: 'Tier 1 Defense',
      description: 'Top-tier defensive operations against nation-state threats.',
    },
    {
      id: 'tier_1_offense',
      label: 'Tier 1 Offense',
      description: 'Authorized threat-emulation planning for defensive validation only.',
    },
    {
      id: 'tier_1_elicitation_expert',
      label: 'Tier 1 Elicitation Expert',
      description: 'Defensive elicitation-risk assessment to determine Dubai threat posture.',
    },
  ],
  legend_parley: [
    {
      id: 'negotiation_path',
      label: 'Negotiation Path',
      description: 'Full Parley playbook from case pack to winning path.',
    },
  ],
};

export const DEFAULT_SPECIALTY_BY_MODE = {
  social_engineering_consultant: 'general_advice',
  nation_state: 'tier_1_defense',
  legend_parley: 'negotiation_path',
};

export const getDefaultSpecialtyForMode = (modeId) => (
  DEFAULT_SPECIALTY_BY_MODE[modeId]
  || MODE_SPECIALTIES[modeId]?.[0]?.id
  || DEFAULT_SPECIALTY_BY_MODE[DEFAULT_CONSULTANT_MODE]
);

export const resolveSpecialtyForMode = (modeId, specialtyId) => {
  const options = MODE_SPECIALTIES[modeId] || [];
  if (options.some((item) => item.id === specialtyId)) return specialtyId;
  return getDefaultSpecialtyForMode(modeId);
};



export const CLASSIFICATION_OPTIONS = [

  { value: 'UNCLASSIFIED', label: 'UNCLASSIFIED' },

  { value: 'OFFICIAL', label: 'OFFICIAL' },

  { value: 'CONFIDENTIAL', label: 'CONFIDENTIAL' },

  { value: 'SECRET', label: 'SECRET' },

  { value: '__custom__', label: 'Custom marking…' },

];



export const REVIEWER_ROLE_OPTIONS = [

  { value: '', label: 'Reviewer role (optional)' },

  { value: 'author', label: 'Author' },

  { value: 'reviewer', label: 'Reviewer' },

  { value: 'approver', label: 'Approver' },

];



/** Placeholder webhook URL for SIEM/ticketing integrations (configure via SEC_AI_INTEGRATION_WEBHOOK_URL). */

export const INTEGRATION_WEBHOOK_PLACEHOLDER = 'https://example.invalid/hooks/sec-ai-exercise';




export const DEFAULT_CONSULTANT_MODE = 'social_engineering_consultant';



export const CHAT_FOLLOWUP = {

  toggleLabel: 'Ask about this answer',

  hint: 'Highlight specific text in the answer above, or ask about the whole reply.',

  excerptLabel: 'You selected',

  noExcerptHint: 'Tip: highlight a sentence or phrase in the answer, then type your question.',

  clearExcerptLabel: 'Clear selection',

  questionLabel: 'Your question',

  questionPlaceholder: 'e.g. Why did you recommend this control instead of X? What evidence supports this?',

  sendLabel: 'Ask',

};



export const SE_BRAND = {

  companyName: 'LEGEND',

  productName: 'LEGEND',

  parentTagline: 'Legend Sovereign and Legend Parley',

  website: 'asksunshine.com',

  websiteUrl: 'https://asksunshine.com',

  phone: '',

  logo: '/branding/legend-sovereign-wordmark-navy.png',

  accentColor: '#159A9C',

};



/** Public auth pages (login, signup, setup) — benign Sunshine branding only. */
export const PUBLIC_AUTH_BRAND = {
  productName: 'Sunshine',
  companyName: 'Sunshine',
  tagline: 'Sign in to continue.',
  logo: '/branding/sunshine-logo.svg',
  website: 'asksunshine.com',
  websiteUrl: 'https://asksunshine.com',
};



export const REPORT_PROGRESS_STEPS = [

  'Extracting pretexts and scripts…',

  'Building detailed fact inventory…',

  'Generating comprehensive report…',

  'Finalizing deliverable…',

];



export const PRETEXT_CHANNELS = [

  'email',

  'phone',

  'sms',

  'voicemail',

  'linkedin',

  'whatsapp',

  'telegram',

  'signal',

  'facebook',

  'instagram',

  'twitter',

  'video-call',

  'in-person',

  'in-person-meeting',

  'postal-mail',

  'conference-event',

  'multi-channel',

];



export const PRETEXT_WIZARD_SECTIONS = [

  {

    id: 'identity',

    title: '1. Pretext Identity',

    fields: [

      ['persona_name', 'Persona name'],

      ['persona_role', 'Persona role'],

      ['persona_org', 'Organizational affiliation'],

      ['credibility_reason', 'Why the target finds this persona credible'],

      ['supporting_legend', 'Supporting legend / backstory'],

      ['channel', 'Communication channel'],

    ],

  },

  {

    id: 'primary',

    title: '2. Primary Objective',

    fields: [

      ['primary_objective', 'Primary objective'],

      ['success_definition', 'Success definition'],

      ['kill_chain_stage', 'Kill chain stage'],

    ],

  },

  {

    id: 'secondary',

    title: '3. Secondary Objectives',

    fields: [

      ['secondary_objectives', 'Secondary objectives / intelligence to gather'],

      ['relationship_access', 'Relationship or access to leverage later'],

      ['cover_maintenance', 'Cover maintenance if attempt fails'],

    ],

  },

  {

    id: 'opening',

    title: '4. Opening Hook',

    fields: [

      ['opening_hook', 'Opening hook (first 30 seconds / message)'],

      ['opening_trigger', 'Influence trigger (urgency, authority, etc.)'],

      ['credibility_establishment', 'Immediate credibility before disengagement'],

    ],

  },

  {

    id: 'flow',

    title: '5. Conversation Flow',

    fields: [

      ['conversation_waypoints', 'Key waypoints to reach the objective'],

      ['pivot_points', 'Natural pivot points'],

      ['resistance_responses', 'Responses to likely resistance'],

      ['escalation_path', 'Escalation path if primary approach stalls'],

    ],

  },

  {

    id: 'delivery',

    title: '6. Objective Delivery Moment',

    fields: [

      ['objective_timing', 'When and how to make the ask'],

      ['objective_language', 'Precise language for the ask'],

      ['compliance_indicators', 'Full vs partial compliance vs shutdown'],

    ],

  },

  {

    id: 'exit',

    title: '7. Exit Strategy',

    fields: [

      ['exit_success', 'Disengage after success'],

      ['exit_failure', 'Disengage without burning legend'],

      ['follow_on_contact', 'Follow-on contact framing'],

    ],

  },

  {

    id: 'failure',

    title: '8. What Failure Looks Like',

    fields: [

      [

        'failure_indicators',

        'Detection / IR / SOC signals that the exercise failed or the adversary succeeded',

      ],

    ],

  },

  {

    id: 'risk',

    title: '9. Risk Flags',

    fields: [

      ['risk_flags', 'Suspicion indicators'],

      ['hard_stops', 'Hard stops / abort criteria'],

      ['shutdown_insights', 'What shutdown reveals about target posture'],

    ],

  },

  {

    id: 'metrics',

    title: '10. Metrics for Debrief',

    fields: [

      ['debrief_metrics', 'Obtained vs attempted / sentiment arc'],

      ['follow_on_pretext', 'Recommended follow-on pretext'],

    ],

  },

];



const operationalFieldKeys = PRETEXT_WIZARD_SECTIONS.flatMap((section) =>

  section.fields.map(([key]) => key).filter((key) => key !== 'channel'),

);



export const EMPTY_PRETEXT_DRAFT = {

  name: '',

  channel: 'email',

  target_audience: '',

  objective: '',

  script: '',

  supporting_details: '',

  tags: '',

  ...Object.fromEntries(operationalFieldKeys.map((key) => [key, ''])),

};



export const pretextToDraft = (pretext) => ({

  ...EMPTY_PRETEXT_DRAFT,

  ...Object.fromEntries(

    Object.keys(EMPTY_PRETEXT_DRAFT).map((key) => [

      key,

      key === 'tags' ? (pretext.tags || []).join(', ') : (pretext[key] || ''),

    ]),

  ),

});



export const DELIVERABLE_STATUSES = [

  { value: 'draft', label: 'Draft' },

  { value: 'in_review', label: 'In Review' },

  { value: 'approved', label: 'Approved' },

  { value: 'released', label: 'Released' },

];



export const LOCALE_OPTIONS = [

  { code: 'auto', label: 'Auto (infer from language)' },

  { code: 'en-US', label: 'English (United States)' },

  { code: 'en-GB', label: 'English (United Kingdom)' },

  { code: 'ar-AE', label: 'Arabic (UAE / Gulf)' },

  { code: 'ar-SA', label: 'Arabic (Modern Standard)' },

  { code: 'tr-TR', label: 'Turkish (Turkey)' },

  { code: 'he-IL', label: 'Hebrew (Israel)' },

  { code: 'fr-FR', label: 'French (France)' },

  { code: 'de-DE', label: 'German (Germany)' },

  { code: 'es-ES', label: 'Spanish (Spain)' },

  { code: 'pt-BR', label: 'Portuguese (Brazil)' },

  { code: 'zh-CN', label: 'Chinese (Simplified)' },

  { code: 'ja-JP', label: 'Japanese (Japan)' },

];



export const REGISTER_OPTIONS = [

  { code: 'operational', label: 'Operational planning' },

  { code: 'executive', label: 'Executive brief' },

  { code: 'formal_ic', label: 'Formal IC / intelligence brief' },

];



export const WORKSPACE_PANELS_STORAGE_KEY = 'sec_ai_workspace_panels';

export const ADMIN_VIEW_STORAGE_KEY = 'sec_ai_admin_view';

export const getStoredAdminView = () => {
  try {
    const raw = sessionStorage.getItem(ADMIN_VIEW_STORAGE_KEY);
    if (!raw) {
      return { open: false, tab: 'organizations' };
    }
    const parsed = JSON.parse(raw);
    return {
      open: Boolean(parsed.open),
      tab: parsed.tab || 'organizations',
    };
  } catch {
    return { open: false, tab: 'organizations' };
  }
};

export const setStoredAdminView = ({ open, tab }) => {
  sessionStorage.setItem(ADMIN_VIEW_STORAGE_KEY, JSON.stringify({
    open: Boolean(open),
    tab: tab || 'organizations',
  }));
};

export const clearStoredAdminView = () => {
  sessionStorage.removeItem(ADMIN_VIEW_STORAGE_KEY);
};

export const WORKSPACE_PANELS = [
  {
    id: 'promptLibrary',
    label: 'Mandate Library',
    description: 'Save and apply reusable operational mandates',
    defaultOpen: false,
  },
  {
    id: 'reportPromptLibrary',
    label: 'Report Prompts',
    description: 'Save and apply report-generation directives',
    defaultOpen: false,
  },
  {
    id: 'engagement',
    label: 'Client',
    description: 'Client, languages, and report template',
    defaultOpen: true,
  },
  {
    id: 'glossary',
    label: 'Glossary',
    description: 'Lock terms and client translations',
    defaultOpen: false,
  },
  {
    id: 'pretext',
    label: 'Pretexts',
    description: 'Pretext studio and extract from chat',
    defaultOpen: true,
  },
  {
    id: 'knowledge',
    label: 'KB',
    description: 'Knowledge base status and uploads',
    defaultOpen: false,
  },
  {
    id: 'team',
    label: 'Team',
    description: 'Manage org users and access keys',
    defaultOpen: false,
    orgAdminOnly: true,
  },
];

export function getDefaultWorkspacePanels() {
  return WORKSPACE_PANELS.reduce((acc, panel) => {
    acc[panel.id] = panel.defaultOpen;
    return acc;
  }, {});
}

export function loadWorkspacePanels() {
  try {
    const raw = localStorage.getItem(WORKSPACE_PANELS_STORAGE_KEY);
    if (!raw) return getDefaultWorkspacePanels();
    const parsed = JSON.parse(raw);
    const defaults = getDefaultWorkspacePanels();
    return { ...defaults, ...parsed };
  } catch {
    return getDefaultWorkspacePanels();
  }
}

export const REPORT_REVIEW_MODES = [

  { code: 'side_by_side', label: 'Side-by-side review' },

  { code: 'original', label: 'Original only' },

  { code: 'translated', label: 'Translation only' },

];



export const EXPORT_LANGUAGE_MODES = [

  { code: 'target_only', label: 'Target language only' },

  { code: 'analyst_only', label: 'Analyst language only' },

  { code: 'side_by_side', label: 'Side-by-side bilingual' },

];



export const LIFECYCLE_STATUSES = [

  { value: 'planning', label: 'Planning' },

  { value: 'authorized', label: 'Authorized' },

  { value: 'active', label: 'Active' },

  { value: 'debrief', label: 'Debrief' },

  { value: 'closed', label: 'Closed' },

  { value: 'archived', label: 'Archived' },

];



export const LANGUAGE_OPTIONS = [

  { code: 'auto', label: 'Auto (match user)' },

  { code: 'en', label: 'English' },

  { code: 'ar', label: 'Arabic' },

  { code: 'es', label: 'Spanish' },

  { code: 'fr', label: 'French' },

  { code: 'de', label: 'German' },

  { code: 'tr', label: 'Turkish' },

  { code: 'he', label: 'Hebrew' },

  { code: 'zh', label: 'Chinese (Simplified)' },

  { code: 'ja', label: 'Japanese' },

  { code: 'ko', label: 'Korean' },

  { code: 'pt', label: 'Portuguese' },

  { code: 'ru', label: 'Russian' },

  { code: 'it', label: 'Italian' },

  { code: 'nl', label: 'Dutch' },

  { code: 'hi', label: 'Hindi' },

  { code: 'ur', label: 'Urdu' },

];



/** Full language list for Tier 1 screen/report selectors (English label + native script). */
export const FULL_LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'ar', label: 'Arabic', native: 'العربية' },
  { code: 'es', label: 'Spanish', native: 'Español' },
  { code: 'fr', label: 'French', native: 'Français' },
  { code: 'de', label: 'German', native: 'Deutsch' },
  { code: 'tr', label: 'Turkish', native: 'Türkçe' },
  { code: 'he', label: 'Hebrew', native: 'עברית' },
  { code: 'zh', label: 'Chinese (Simplified)', native: '中文' },
  { code: 'ja', label: 'Japanese', native: '日本語' },
  { code: 'ko', label: 'Korean', native: '한국어' },
  { code: 'pt', label: 'Portuguese', native: 'Português' },
  { code: 'ru', label: 'Russian', native: 'Русский' },
  { code: 'it', label: 'Italian', native: 'Italiano' },
  { code: 'nl', label: 'Dutch', native: 'Nederlands' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'ur', label: 'Urdu', native: 'اردو' },
  { code: 'fa', label: 'Persian', native: 'فارسی' },
  { code: 'pl', label: 'Polish', native: 'Polski' },
  { code: 'uk', label: 'Ukrainian', native: 'Українська' },
  { code: 'vi', label: 'Vietnamese', native: 'Tiếng Việt' },
  { code: 'th', label: 'Thai', native: 'ไทย' },
  { code: 'id', label: 'Indonesian', native: 'Bahasa Indonesia' },
  { code: 'ms', label: 'Malay', native: 'Bahasa Melayu' },
  { code: 'bn', label: 'Bengali', native: 'বাংলা' },
  { code: 'ta', label: 'Tamil', native: 'தமிழ்' },
  { code: 'te', label: 'Telugu', native: 'తెలుగు' },
  { code: 'pa', label: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  { code: 'gu', label: 'Gujarati', native: 'ગુજરાતી' },
  { code: 'mr', label: 'Marathi', native: 'मराठी' },
  { code: 'kn', label: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'ml', label: 'Malayalam', native: 'മലയാളം' },
  { code: 'si', label: 'Sinhala', native: 'සිංහල' },
  { code: 'ne', label: 'Nepali', native: 'नेपाली' },
  { code: 'my', label: 'Burmese', native: 'မြန်မာဘာသာ' },
  { code: 'km', label: 'Khmer', native: 'ភាសាខ្មែរ' },
  { code: 'lo', label: 'Lao', native: 'ລາວ' },
  { code: 'sv', label: 'Swedish', native: 'Svenska' },
  { code: 'no', label: 'Norwegian', native: 'Norsk' },
  { code: 'da', label: 'Danish', native: 'Dansk' },
  { code: 'fi', label: 'Finnish', native: 'Suomi' },
  { code: 'el', label: 'Greek', native: 'Ελληνικά' },
  { code: 'ro', label: 'Romanian', native: 'Română' },
  { code: 'hu', label: 'Hungarian', native: 'Magyar' },
  { code: 'cs', label: 'Czech', native: 'Čeština' },
  { code: 'sk', label: 'Slovak', native: 'Slovenčina' },
  { code: 'bg', label: 'Bulgarian', native: 'Български' },
  { code: 'sr', label: 'Serbian', native: 'Српски' },
  { code: 'hr', label: 'Croatian', native: 'Hrvatski' },
  { code: 'sl', label: 'Slovenian', native: 'Slovenščina' },
  { code: 'lt', label: 'Lithuanian', native: 'Lietuvių' },
  { code: 'lv', label: 'Latvian', native: 'Latviešu' },
  { code: 'et', label: 'Estonian', native: 'Eesti' },
  { code: 'sq', label: 'Albanian', native: 'Shqip' },
  { code: 'ka', label: 'Georgian', native: 'ქართული' },
  { code: 'hy', label: 'Armenian', native: 'Հայերեն' },
  { code: 'az', label: 'Azerbaijani', native: 'Azərbaycanca' },
  { code: 'kk', label: 'Kazakh', native: 'Қазақша' },
  { code: 'uz', label: 'Uzbek', native: 'Oʻzbekcha' },
  { code: 'ps', label: 'Pashto', native: 'پښتو' },
  { code: 'ku', label: 'Kurdish', native: 'Kurdî' },
  { code: 'am', label: 'Amharic', native: 'አማርኛ' },
  { code: 'sw', label: 'Swahili', native: 'Kiswahili' },
  { code: 'so', label: 'Somali', native: 'Soomaali' },
  { code: 'ha', label: 'Hausa', native: 'Hausa' },
  { code: 'af', label: 'Afrikaans', native: 'Afrikaans' },
  { code: 'ca', label: 'Catalan', native: 'Català' },
  { code: 'fil', label: 'Filipino', native: 'Filipino' },
  { code: 'eu', label: 'Basque', native: 'Euskara' },
  { code: 'gl', label: 'Galician', native: 'Galego' },
  { code: 'is', label: 'Icelandic', native: 'Íslenska' },
  { code: 'mt', label: 'Maltese', native: 'Malti' },
  { code: 'be', label: 'Belarusian', native: 'Беларуская' },
  { code: 'mn', label: 'Mongolian', native: 'Монгол' },
  { code: 'tg', label: 'Tajik', native: 'Тоҷикӣ' },
  { code: 'ky', label: 'Kyrgyz', native: 'Кыргызча' },
  { code: 'tk', label: 'Turkmen', native: 'Türkmençe' },
  { code: 'ug', label: 'Uyghur', native: 'ئۇيغۇرچە' },
  { code: 'sd', label: 'Sindhi', native: 'سنڌي' },
  { code: 'as', label: 'Assamese', native: 'অসমীয়া' },
  { code: 'or', label: 'Odia', native: 'ଓଡ଼ିଆ' },
  { code: 'yo', label: 'Yoruba', native: 'Yorùbá' },
  { code: 'ig', label: 'Igbo', native: 'Igbo' },
  { code: 'zu', label: 'Zulu', native: 'isiZulu' },
];



/** English first, remaining languages A–Z by English label. */
export const sortLanguageOptionsEnglishFirst = (options) => {
  const list = Array.isArray(options) ? options : [];
  const english = list.filter((item) => item.code === 'en');
  const rest = list
    .filter((item) => item.code !== 'en')
    .sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));
  return [...english, ...rest];
};

export const SORTED_LANGUAGE_OPTIONS = sortLanguageOptionsEnglishFirst(FULL_LANGUAGE_OPTIONS);



export const formatBilingualLanguageLabel = (option) => {
  if (!option) return '';
  if (!option.native || option.native === option.label) {
    return option.label;
  }
  return `${option.label} — ${option.native}`;
};



export const getLanguageLabel = (code) => {

  if (!code) return 'Auto (match user)';

  const match = LANGUAGE_OPTIONS.find((item) => item.code === code);

  return match?.label || code;

};



export const LOCALIZED_WELCOMES = {

  social_engineering_consultant: {

    ar: 'مرحباً! أنا مستشارك الأمني. سأساعدك على فهم مخاطر الهندسة الاجتماعية ومعالجتها. ما التحدي أو المشكلة التي تحاول حلها؟',

    tr: 'Merhaba! Sosyal mühendislik danışmanınızım. Güvenlik endişelerinizi anlamanıza ve ele almanıza yardımcı olacağım. Çözmeye çalıştığınız sorun nedir?',

    es: '¡Hola! Soy su consultor de seguridad. Estoy aquí para ayudarle a comprender y abordar sus preocupaciones de ingeniería social. ¿Qué problema intenta resolver?',

    fr: 'Bonjour ! Je suis votre consultant en sécurité. Je suis là pour vous aider à comprendre et traiter vos préoccupations en matière d\'ingénierie sociale. Quel problème essayez-vous de résoudre ?',

    de: 'Hallo! Ich bin Ihr Sicherheitsberater. Ich helfe Ihnen, Ihre Bedenken zur Social Engineering zu verstehen und anzugehen. Welches Problem möchten Sie lösen?',

  },

  nation_state: {

    ar: 'وضع مستشار الدولة نشط. شاركني ملف الهدف والهدف التشغيلي والقيود. سأطرح أسئلة استراتيجية لتحديد أكثر مسارات الهجوم فعالية. ما هو هدفك؟',

    tr: 'Ulus Devleti Danışman modu aktif. Hedef profilinizi, operasyonel amacınızı ve kısıtlarınızı paylaşın. En etkili saldırı vektörlerini belirlemek için stratejik sorular soracağım. Amacınız nedir?',

    es: 'Modo Consultor de Estado-Nación activo. Comparta el perfil del objetivo, el objetivo operativo y las restricciones. Haré preguntas estratégicas para identificar los vectores de ataque más efectivos. ¿Cuál es su objetivo?',

    fr: 'Mode Consultant État-Nation actif. Partagez le profil de la cible, l\'objectif opérationnel et les contraintes. Je poserai des questions stratégiques pour identifier les vecteurs d\'attaque les plus efficaces. Quel est votre objectif ?',

    de: 'Nation-State-Beratermodus aktiv. Teilen Sie Zielprofil, operatives Ziel und Einschränkungen mit. Ich stelle strategische Fragen, um die wirksamsten Angriffsvektoren zu identifizieren. Was ist Ihr Ziel?',

  },

};



export const getWelcomeMessage = (modeId, languageCode = 'en') => {

  const normalized = !languageCode || languageCode === 'auto' ? 'en' : languageCode;

  const localized = LOCALIZED_WELCOMES[modeId]?.[normalized];

  if (localized) return localized;

  if (modeId === 'nation_state') {
    return 'WRAITH mode active. Choose a specialty and share authorized objectives, scope, and constraints.';
  }
  if (modeId === 'legend_parley') {
    return 'Legend Parley is ready. Upload the case pack, add OSINT or public links, and generate a negotiation playbook.';
  }
  return "Hello! I'm your Apex Elicit consultant. What challenge are you trying to solve?";

};



export const getStoredAuthToken = () => localStorage.getItem(AUTH_TOKEN_KEY);



export const getStoredAuthProfile = () => {

  const raw = localStorage.getItem(AUTH_PROFILE_KEY);

  if (!raw) return null;

  try {

    return normalizeAuthProfile(JSON.parse(raw));

  } catch {

    return null;

  }

};



export const isSuperadminRole = (role) => role === 'superadmin';



export const isOrgAdminRole = (role) => role === 'superadmin' || role === 'admin';



export const canWriteConfig = (profile) => Boolean(profile?.can_write_config ?? isOrgAdminRole(profile?.role));



export const canViewPromptLibraryContent = (profile) => {
  if (!profile) return false;
  if (isOrgAdminRole(profile.role) || isOperatorRole(profile.role)) return true;
  return Boolean(profile.can_write_prompt_library);
};

export const canWritePromptLibrary = (profile) => {
  if (!profile) return false;
  if (isOrgAdminRole(profile.role) || isOperatorRole(profile.role)) return true;
  return Boolean(profile.can_write_prompt_library);
};



export const canManageUsers = (profile) => Boolean(profile?.can_manage_users ?? isOrgAdminRole(profile?.role));



export const isOperatorRole = (role) => role === 'operator';



export const isReadonlyRole = (role) => role === 'readonly';



export const canUseChat = (profile) => Boolean(
  profile?.can_use_chat ?? (isOrgAdminRole(profile?.role) || isOperatorRole(profile?.role)),
);



export const canExportReports = (profile) => Boolean(
  profile?.can_export_reports ?? (
    isOrgAdminRole(profile?.role) || isOperatorRole(profile?.role) || isReadonlyRole(profile?.role)
  ),
);



export const canFeedLlm = (profile) => Boolean(
  profile?.can_feed_llm ?? (isOrgAdminRole(profile?.role) || isOperatorRole(profile?.role)),
);



export const canSyncKb = (profile) => Boolean(
  profile?.can_sync_kb ?? (isOrgAdminRole(profile?.role) || isOperatorRole(profile?.role)),
);



export const canManageKbNoindex = (profile) => Boolean(
  profile?.can_manage_kb_noindex ?? canFeedLlm(profile),
);



export const ADMIN_FEED_ACCEPT = '.json,.pdf,.doc,.docx';



export const OPERATOR_FEED_ACCEPT = '.json,.pdf,.doc,.docx,.txt';



export const SUPERADMIN_FEED_ACCEPT = [
  '.pdf', '.doc', '.docx', '.txt', '.text', '.md', '.markdown',
  '.xlsx', '.xls', '.pptx', '.ppt', '.csv', '.json', '.xml',
  '.html', '.htm', '.rtf', '.zip',
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.tiff', '.tif', '.svg',
  '.mp3', '.wav', '.m4a', '.ogg', '.flac', '.aac', '.wma',
  '.mp4', '.avi', '.mov', '.mkv', '.webm', '.m4v', '.wmv',
  '.yaml', '.yml', '.log', '.ini', '.cfg', '.conf', '.env', '.sql', '.tsv',
  '.js', '.ts', '.py', '.rb', '.go', '.java', '.c', '.cpp', '.h', '.cs', '.sh', '.bat', '.ps1',
].join(',');



export const getFeedFileAccept = (profile) => {
  if (!canFeedLlm(profile)) return '';
  if (Array.isArray(profile?.allowed_feed_extensions) && profile.allowed_feed_extensions.length > 0) {
    return profile.allowed_feed_extensions.join(',');
  }
  if (isSuperadminRole(profile?.role)) return SUPERADMIN_FEED_ACCEPT;
  if (isOrgAdminRole(profile?.role)) return ADMIN_FEED_ACCEPT;
  if (isOperatorRole(profile?.role)) return OPERATOR_FEED_ACCEPT;
  return '';
};

export const formatUploadErrorMessage = (response, errorData, uiLanguage = 'en') => {
  if (response?.status === 413) {
    return tUi(uiLanguage, 'appErrorUploadFileTooLarge');
  }

  const detail = errorData?.detail ?? errorData?.message;
  if (typeof detail === 'string' && detail.trim()) {
    if (response?.status === 403) {
      return detail;
    }
    return detail;
  }

  if (Array.isArray(detail)) {
    const joined = detail.map((item) => item.msg || item.loc || String(item)).join(', ');
    if (joined) return joined;
  }

  if (response?.status === 403) {
    return tUi(uiLanguage, 'appErrorUploadForbidden');
  }

  if (response?.status) {
    return `Server error: ${response.status} ${response.statusText || ''}`.trim();
  }

  return tUi(uiLanguage, 'appErrorUploadFiles');
};



export const storeAuth = (loginResponse) => {
  const previousProfile = getStoredAuthProfile();
  const profile = normalizeAuthProfile({
    tenant_id: loginResponse.tenant_id,
    tenant_name: loginResponse.tenant_name,
    is_platform_admin: loginResponse.is_platform_admin,
    role: loginResponse.role || (loginResponse.is_platform_admin ? 'superadmin' : 'admin'),
    user_id: loginResponse.user_id || null,
    user_email: loginResponse.user_email || null,
    user_name: loginResponse.user_name || null,
    can_manage_users: loginResponse.can_manage_users ?? isOrgAdminRole(loginResponse.role || (loginResponse.is_platform_admin ? 'superadmin' : 'admin')),
    can_write_config: loginResponse.can_write_config ?? isOrgAdminRole(loginResponse.role || (loginResponse.is_platform_admin ? 'superadmin' : 'admin')),
    can_write_prompt_library: loginResponse.can_write_prompt_library ?? (
      isOrgAdminRole(loginResponse.role || (loginResponse.is_platform_admin ? 'superadmin' : 'admin'))
      || isOperatorRole(loginResponse.role)
    ),
    can_use_chat: loginResponse.can_use_chat ?? (
      isOrgAdminRole(loginResponse.role || (loginResponse.is_platform_admin ? 'superadmin' : 'admin'))
      || isOperatorRole(loginResponse.role)
    ),
    can_export_reports: loginResponse.can_export_reports ?? (
      isOrgAdminRole(loginResponse.role || (loginResponse.is_platform_admin ? 'superadmin' : 'admin'))
      || isOperatorRole(loginResponse.role)
      || isReadonlyRole(loginResponse.role)
    ),
    can_feed_llm: loginResponse.can_feed_llm ?? (
      isOrgAdminRole(loginResponse.role || (loginResponse.is_platform_admin ? 'superadmin' : 'admin'))
      || isOperatorRole(loginResponse.role)
    ),
    can_sync_kb: loginResponse.can_sync_kb ?? (
      isOrgAdminRole(loginResponse.role || (loginResponse.is_platform_admin ? 'superadmin' : 'admin'))
      || isOperatorRole(loginResponse.role)
    ),
    allowed_feed_extensions: loginResponse.allowed_feed_extensions || (
      isSuperadminRole(loginResponse.role || (loginResponse.is_platform_admin ? 'superadmin' : 'admin'))
        ? SUPERADMIN_FEED_ACCEPT.split(',')
        : isOrgAdminRole(loginResponse.role || (loginResponse.is_platform_admin ? 'superadmin' : 'admin'))
          ? ADMIN_FEED_ACCEPT.split(',')
          : isOperatorRole(loginResponse.role)
            ? OPERATOR_FEED_ACCEPT.split(',')
            : []
    ),
    product_entitlement: loginResponse.product_entitlement,
    allowed_modes: loginResponse.allowed_modes,
    default_mode: loginResponse.default_mode,
  });

  localStorage.setItem(AUTH_TOKEN_KEY, loginResponse.access_token);
  localStorage.setItem(AUTH_PROFILE_KEY, JSON.stringify(profile));

  const tenantChanged = previousProfile?.tenant_id
    && previousProfile.tenant_id !== profile.tenant_id;
  const entitlementChanged = previousProfile?.product_entitlement
    && previousProfile.product_entitlement !== profile.product_entitlement;
  if (tenantChanged || entitlementChanged) {
    if (previousProfile?.tenant_id) {
      clearStoredActiveSessionForTenant(previousProfile.tenant_id);
    }
    if (tenantChanged && profile.tenant_id) {
      clearStoredActiveSessionForTenant(profile.tenant_id);
    }
  } else if (profile.tenant_id) {
    getStoredActiveSession(profile.tenant_id);
  }
};



export const clearAuth = () => {

  localStorage.removeItem(AUTH_TOKEN_KEY);

  localStorage.removeItem(AUTH_PROFILE_KEY);

};



export const hasLegacyApiKeyAuth = () => Boolean(process.env.REACT_APP_SEC_AI_API_KEY);



export const isAuthenticated = () => Boolean(getStoredAuthToken() || hasLegacyApiKeyAuth());



export const getApiHeaders = (json = true) => {

  const headers = {};

  if (json) headers['Content-Type'] = 'application/json';

  const token = getStoredAuthToken();

  if (token) headers.Authorization = `Bearer ${token}`;

  const apiKey = process.env.REACT_APP_SEC_AI_API_KEY;

  if (apiKey) headers['X-API-Key'] = apiKey;

  return headers;

};



/**
 * Turn FastAPI error `detail` (string or structured object) into user-visible text.
 */
export const formatApiErrorDetail = (detail, fallback = 'Request failed') => {
  if (detail == null || detail === '') return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        const loc = (item?.loc || []).filter((part) => part !== 'body').join('.');
        const msg = item?.msg || '';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join('; ') || fallback;
  }
  if (typeof detail === 'object') {
    const parts = [];
    if (detail.message) parts.push(detail.message);
    const phase = detail.debug?.phase;
    if (phase && !String(detail.message || '').includes(phase)) {
      const phaseLabels = {
        pretext_extraction: 'pretext extraction',
        detail_extraction: 'detail extraction',
        llm_generation: 'report generation',
        llm_invoke: 'report generation',
        structure_validation: 'structure validation',
        schema_validation: 'schema validation',
        translation_baseline: 'English baseline translation',
        translation_client: 'client language translation',
        json_parse: 'JSON parsing',
        json_normalize: 'JSON normalization',
        cancelled: 'cancelled',
      };
      parts.push(`(during ${phaseLabels[phase] || phase})`);
    }
    if (detail.debug?.error) {
      parts.push(String(detail.debug.error));
    }
    const fields = detail.debug?.fields;
    if (Array.isArray(fields) && fields.length > 0) {
      const hints = fields.slice(0, 3).map((item) => {
        const loc = (item?.loc || []).join('.');
        const msg = item?.msg || '';
        return loc ? `${loc}: ${msg}` : msg;
      });
      if (hints.length) parts.push(hints.join('; '));
    }
    if (parts.length) return parts.join(' — ');
    if (detail.error) return String(detail.error);
  }
  return fallback;
};

/** Parse JSON; throw a clear error when nginx/SPA returns HTML instead of an API payload. */
export const parseJsonResponse = async (response, fallback = {}) => {
  const contentType = response.headers.get('content-type') || '';
  let text;
  try {
    text = await response.text();
  } catch {
    return fallback;
  }
  const trimmed = text.trimStart();
  const looksLikeHtml = trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html');
  if (!contentType.includes('application/json')) {
    if (looksLikeHtml) {
      if (response.status === 524 || response.status === 504) {
        throw new Error(
          'The translation request timed out at the edge network (HTTP '
          + response.status
          + '). Refresh the page — translation may still be running on the server. '
          + 'If the report is unchanged, try Translate again.',
        );
      }
      throw new Error(
        response.status === 404
          ? 'API endpoint not found (server returned the web app instead of JSON). Deploy the latest backend.'
          : `Server returned HTML instead of JSON (HTTP ${response.status}). Check API routing or the /api nginx proxy.`,
      );
    }
    return fallback;
  }
  if (!text) return fallback;
  try {
    return JSON.parse(text);
  } catch {
    if (looksLikeHtml) {
      throw new Error('Server returned HTML instead of JSON.');
    }
    return fallback;
  }
};

/** User-visible message from a failed apiFetch response (after parseJsonResponse). */
export const apiResponseError = (response, data, fallback = 'Request failed') =>
  formatApiErrorDetail(data?.detail, fallback || `Request failed (HTTP ${response.status})`);

/** True when assistant text is heading-only or too short to show operators. */
export const isDegenerateAssistantContent = (content) => {
  const stripped = (content || '').trim();
  if (!stripped) return true;
  if (/^#{1,6}\s*$/m.test(stripped) && stripped.replace(/^#{1,6}\s*$/gm, '').trim() === '') {
    return true;
  }
  const substance = stripped
    .replace(/^#{1,6}\s+.*$/gm, '')
    .replace(/[*_`>#\-•]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!substance) return true;
  return stripped.length < 20 || substance.length < 12;
};

/** Map low-level fetch/stream failures to a clearer operator message. */
export const formatNetworkErrorMessage = (err, fallback = 'Request failed') => {
  const message = (err && err.message) || '';
  if (/failed to fetch|networkerror|load failed|network error/i.test(message)) {
    return (
      'Connection lost while waiting for the consultant. '
      + 'The server may still be working — refresh and check chat history before resending.'
    );
  }
  return message || fallback;
};

/** Consume POST /api/chat SSE stream (stream: true). Calls onToken for each chunk. */
export const consumeChatStream = async (response, { onToken, onReplace, signal } = {}) => {
  if (!response.body) {
    throw new Error('Streaming not supported in this browser');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      if (signal?.aborted) {
        await reader.cancel();
        throw new DOMException('Aborted', 'AbortError');
      }
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) {
          continue;
        }
        const payload = trimmed.slice(5).trim();
        if (!payload) {
          continue;
        }
        let event;
        try {
          event = JSON.parse(payload);
        } catch {
          continue;
        }
        if (event.type === 'token' && event.content) {
          onToken?.(event.content);
        } else if (event.type === 'replace' && event.content != null) {
          onReplace?.(event.content);
        } else if (event.type === 'error') {
          throw new Error(event.detail || 'Chat stream failed');
        } else if (event.type === 'done') {
          return event;
        }
      }
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    throw new Error(formatNetworkErrorMessage(err, err.message || 'Chat stream failed'));
  }
  throw new Error('Chat stream ended without a completion event');
};

/** Poll GET /api/chat/status for a background chat job (202 Accepted flow). */
export const pollChatJobStatus = async (sessionId, { signal } = {}) => {
  const params = new URLSearchParams({
    session_id: sessionId,
    _t: String(Date.now()),
  });
  const response = await apiFetch(`/api/chat/status?${params.toString()}`, {
    signal,
    cache: 'no-store',
  });
  if (!response.ok) {
    const errorData = await parseJsonResponse(response);
    throw new Error(
      (errorData && errorData.detail) || 'Failed to check chat status',
    );
  }
  return response.json();
};

/** Wait for a queued chat job; calls onStreamedText as the model produces output. */
export const waitForChatJobCompletion = async (
  sessionId,
  { signal, onStreamedText } = {},
) => {
  const deadline = Date.now() + 120 * 60 * 1000;
  let lastText = '';
  while (true) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    const status = await pollChatJobStatus(sessionId, { signal });
    const streamed = status.streamed_text || '';
    if (streamed && streamed !== lastText) {
      lastText = streamed;
      onStreamedText?.(streamed);
    }
    if (status.status === 'completed' && status.response) {
      return status.response;
    }
    if (status.status === 'failed') {
      throw new Error(status.error || status.message || 'Chat failed');
    }
    if (status.status === 'cancelled') {
      throw new Error(status.message || 'Chat was cancelled');
    }
    if (Date.now() >= deadline) {
      throw new Error(
        'Chat timed out after 2 hours. Refresh and check history before resending.',
      );
    }
    await new Promise((resolve) => setTimeout(resolve, 2000));
  }
};

export const apiFetch = async (path, options = {}) => {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        ...getApiHeaders(options.body !== undefined && !(options.body instanceof FormData)),
        ...(options.headers || {}),
      },
    });
  } catch (err) {
    const message = (err && err.message) || '';
    if (/failed to fetch|networkerror|load failed|network error/i.test(message)) {
      throw new Error(formatNetworkErrorMessage(err));
    }
    throw err;
  }

  if (response.status === 401 && getStoredAuthToken()) {
    clearAuth();
  }

  return response;
};

/** Upload session files with XMLHttpRequest so upload progress can be reported. */
export const uploadSessionFilesWithProgress = (
  { sessionId, files, pdfPasswords = {}, onProgress, uploadKind = 'knowledge' },
) => new Promise((resolve, reject) => {
  if (!sessionId) {
    reject(new Error('Session is not ready. Refresh the page and try again.'));
    return;
  }
  if (!files?.length) {
    reject(new Error('No files selected'));
    return;
  }

  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  formData.append('session_id', sessionId);
  if (uploadKind) {
    formData.append('upload_kind', uploadKind);
  }
  if (pdfPasswords && Object.keys(pdfPasswords).length > 0) {
    formData.append('pdf_passwords', JSON.stringify(pdfPasswords));
  }

  const xhr = new XMLHttpRequest();
  xhr.open('POST', `${API_BASE_URL}/api/upload_file`);

  const headers = getApiHeaders(false);
  Object.entries(headers).forEach(([key, value]) => {
    if (value != null && value !== '') {
      xhr.setRequestHeader(key, value);
    }
  });

  xhr.upload.addEventListener('progress', (event) => {
    if (!onProgress) return;
    if (event.lengthComputable && event.total > 0) {
      onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
    } else if (event.loaded > 0) {
      onProgress(50);
    }
  });

  xhr.addEventListener('load', () => {
    let payload = {};
    try {
      payload = xhr.responseText ? JSON.parse(xhr.responseText) : {};
    } catch {
      reject(new Error('Upload failed: invalid server response'));
      return;
    }
    if (xhr.status >= 200 && xhr.status < 300) {
      resolve(payload);
      return;
    }
    reject(new Error(formatApiErrorDetail(payload.detail, `Upload failed (${xhr.status})`)));
  });

  xhr.addEventListener('error', () => reject(new Error('Upload failed: network error')));
  xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
  xhr.send(formData);
});



export const createSessionFromApi = async (mode = DEFAULT_CONSULTANT_MODE, specialty = '') => {

  const params = new URLSearchParams({ mode });
  if (specialty) params.set('specialty', specialty);
  const response = await apiFetch(`/api/sessions?${params.toString()}`, {

    method: 'POST',

  });

  if (!response.ok) {

    const errorData = await parseJsonResponse(response);

    throw new Error(errorData.detail || 'Failed to create session');

  }

  const data = await parseJsonResponse(response);

  if (!data.session_id) {
    throw new Error('Failed to create session');
  }

  return data.session_id;

};


